import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { adminService } from '@/lib/api/services/admin.service';
import { Loader2, Save, Trash2, RefreshCw, Search, FileText, Edit2, X, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export function ChatConfig() {
  const queryClient = useQueryClient();
  const [aiSuggestionsPromptFreelancer, setAiSuggestionsPromptFreelancer] = useState('');
  const [aiSuggestionsPromptClient, setAiSuggestionsPromptClient] = useState('');
  const [aiHelperPromptFreelancer, setAiHelperPromptFreelancer] = useState('');
  const [aiHelperPromptClient, setAiHelperPromptClient] = useState('');
  const [messageCount, setMessageCount] = useState(5);
  const [emailThreshold, setEmailThreshold] = useState(2);
  const [ragEnabled, setRagEnabled] = useState(true);
  const [analysisBatchSize, setAnalysisBatchSize] = useState(10);
  const [modelId, setModelId] = useState('anthropic.claude-3-5-haiku-20241022');
  const [knowledgeBaseId, setKnowledgeBaseId] = useState('');
  const [dataSourceId, setDataSourceId] = useState('');
  const [maxRetrievalResults, setMaxRetrievalResults] = useState(5);
  const [showPromptsConfirm, setShowPromptsConfirm] = useState(false);
  const [showGeneralConfirm, setShowGeneralConfirm] = useState(false);
  const [showBedrockConfirm, setShowBedrockConfirm] = useState(false);
  const [pendingSection, setPendingSection] = useState<'prompts' | 'general' | 'bedrock' | null>(null);
  
  // Edit mode states
  const [isEditingPrompts, setIsEditingPrompts] = useState(false);
  const [isEditingGeneral, setIsEditingGeneral] = useState(false);
  const [isEditingBedrock, setIsEditingBedrock] = useState(false);
  
  // Original values for cancel
  const [originalPrompts, setOriginalPrompts] = useState({
    aiSuggestionsPromptFreelancer: '',
    aiSuggestionsPromptClient: '',
    aiHelperPromptFreelancer: '',
    aiHelperPromptClient: '',
  });
  const [originalGeneral, setOriginalGeneral] = useState({
    messageCount: 5,
    emailThreshold: 2,
    ragEnabled: true,
    analysisBatchSize: 10,
    maxRetrievalResults: 5,
  });
  const [originalBedrock, setOriginalBedrock] = useState({
    modelId: '',
    knowledgeBaseId: '',
    dataSourceId: '',
  });
  
  // Knowledge Base state
  const [testQuery, setTestQuery] = useState('');
  const [testResults, setTestResults] = useState<any[] | null>(null);
  
  const { data: documents, isLoading: loadingDocuments } = useQuery({
    queryKey: ['admin', 'knowledge-base', 'documents'],
    queryFn: () => adminService.getDocuments(),
  });

  const { data: syncStatus } = useQuery({
    queryKey: ['admin', 'knowledge-base', 'sync-status'],
    queryFn: () => adminService.getSyncStatus(),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => adminService.uploadDocument(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'knowledge-base', 'documents'] });
      toast.success('Document uploaded successfully');
    },
    onError: () => {
      toast.error('Failed to upload document');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminService.deleteDocument(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'knowledge-base', 'documents'] });
      toast.success('Document deleted successfully');
    },
    onError: () => {
      toast.error('Failed to delete document');
    },
  });

  const syncMutation = useMutation({
    mutationFn: (id: string) => adminService.syncDocument(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'knowledge-base', 'documents'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'knowledge-base', 'sync-status'] });
      toast.success('Sync started successfully');
    },
    onError: () => {
      toast.error('Failed to start sync');
    },
  });

  const testQueryMutation = useMutation({
    mutationFn: (query: string) => adminService.testQuery(query),
    onSuccess: (results) => {
      setTestResults(results);
      toast.success('Query executed successfully');
    },
    onError: () => {
      toast.error('Failed to execute query');
    },
  });

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadMutation.mutate(file);
    }
  };

  const handleTestQuery = () => {
    if (testQuery.trim()) {
      testQueryMutation.mutate(testQuery.trim());
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-emerald-700 bg-emerald-100';
      case 'syncing':
        return 'text-[#4669ed] bg-[rgba(70,105,237,0.10)]';
      case 'failed':
        return 'text-red-700 bg-red-100';
      default:
        return 'text-slate-700 bg-slate-100';
    }
  };

  // Helper function to parse config value
  const parseConfigValue = (value: any): any => {
    if (value === null || value === undefined) {
      return '';
    }
    
    // If it's already not a string, return as-is
    if (typeof value !== 'string') {
      return value;
    }
    
    // Remove surrounding quotes if present
    let cleaned = value.trim();
    if ((cleaned.startsWith('"') && cleaned.endsWith('"')) || 
        (cleaned.startsWith("'") && cleaned.endsWith("'"))) {
      cleaned = cleaned.slice(1, -1);
    }
    
    // Try to parse as JSON
    try {
      const parsed = JSON.parse(cleaned);
      return parsed;
    } catch {
      // Not valid JSON, return the cleaned string
      return cleaned;
    }
  };

  const { data: config, isLoading } = useQuery({
    queryKey: ['admin', 'config'],
    queryFn: () => adminService.getConfig(),
  });

  // Update state when config data changes
  useEffect(() => {
    if (!config || !Array.isArray(config)) {
      return;
    }

    // Create a map of config values with proper parsing
    const configMap = config.reduce((acc: Record<string, any>, item: any) => {
      if (item && item.key) {
        acc[item.key] = parseConfigValue(item.value);
      }
      return acc;
    }, {});

    // Set state values with proper type conversion
    const promptsFreelancer = String(configMap.ai_suggestions_system_prompt_freelancer || '');
    const promptsClient = String(configMap.ai_suggestions_system_prompt_client || '');
    const helperFreelancer = String(configMap.ai_helper_system_prompt_freelancer || '');
    const helperClient = String(configMap.ai_helper_system_prompt_client || '');
    const msgCount = Number(configMap.ai_suggestions_message_count) || 5;
    const emailThresh = Number(configMap.email_notification_threshold_hours) || 2;
    const rag = configMap.rag_enabled === 'true' || 
      configMap.rag_enabled === true ||
      String(configMap.rag_enabled).toLowerCase() === 'true';
    const batchSize = Number(configMap.analysis_batch_size) || 10;
    const model = String(configMap.ai_model || 'anthropic.claude-3-5-haiku-20241022');
    const kbId = String(configMap.bedrock_knowledge_base_id || '');
    const dsId = String(configMap.bedrock_data_source_id || '');
    const maxRet = Number(configMap.max_retrieval_results) || 5;

    setAiSuggestionsPromptFreelancer(promptsFreelancer);
    setAiSuggestionsPromptClient(promptsClient);
    setAiHelperPromptFreelancer(helperFreelancer);
    setAiHelperPromptClient(helperClient);
    setMessageCount(msgCount);
    setEmailThreshold(emailThresh);
    setRagEnabled(rag);
    setAnalysisBatchSize(batchSize);
    setModelId(model);
    setKnowledgeBaseId(kbId);
    setDataSourceId(dsId);
    setMaxRetrievalResults(maxRet);

    // Save original values
    setOriginalPrompts({
      aiSuggestionsPromptFreelancer: promptsFreelancer,
      aiSuggestionsPromptClient: promptsClient,
      aiHelperPromptFreelancer: helperFreelancer,
      aiHelperPromptClient: helperClient,
    });
    setOriginalGeneral({
      messageCount: msgCount,
      emailThreshold: emailThresh,
      ragEnabled: rag,
      analysisBatchSize: batchSize,
      maxRetrievalResults: maxRet,
    });
    setOriginalBedrock({
      modelId: model,
      knowledgeBaseId: kbId,
      dataSourceId: dsId,
    });
  }, [config]);

  const updateConfigMutation = useMutation({
    mutationFn: async (updates: Array<{ key: string; value: string | number | boolean }>) => {
      // Use bulk update API
      return adminService.updateConfigBulk(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'config'] });
      toast.success('Configuration updated successfully');
      // Exit edit mode after successful save
      setIsEditingPrompts(false);
      setIsEditingGeneral(false);
      setIsEditingBedrock(false);
    },
    onError: () => {
      toast.error('Failed to update configuration');
    },
  });

  const handleEditPrompts = () => {
    setIsEditingPrompts(true);
  };

  const handleCancelPrompts = () => {
    setAiSuggestionsPromptFreelancer(originalPrompts.aiSuggestionsPromptFreelancer);
    setAiSuggestionsPromptClient(originalPrompts.aiSuggestionsPromptClient);
    setAiHelperPromptFreelancer(originalPrompts.aiHelperPromptFreelancer);
    setAiHelperPromptClient(originalPrompts.aiHelperPromptClient);
    setIsEditingPrompts(false);
  };

  const handleEditGeneral = () => {
    setIsEditingGeneral(true);
  };

  const handleCancelGeneral = () => {
    setMessageCount(originalGeneral.messageCount);
    setEmailThreshold(originalGeneral.emailThreshold);
    setRagEnabled(originalGeneral.ragEnabled);
    setAnalysisBatchSize(originalGeneral.analysisBatchSize);
    setMaxRetrievalResults(originalGeneral.maxRetrievalResults);
    setIsEditingGeneral(false);
  };

  const handleEditBedrock = () => {
    setIsEditingBedrock(true);
  };

  const handleCancelBedrock = () => {
    setModelId(originalBedrock.modelId);
    setKnowledgeBaseId(originalBedrock.knowledgeBaseId);
    setDataSourceId(originalBedrock.dataSourceId);
    setIsEditingBedrock(false);
  };

  const handleSavePrompts = () => {
    setPendingSection('prompts');
    setShowPromptsConfirm(true);
  };

  const handleSaveGeneral = () => {
    setPendingSection('general');
    setShowGeneralConfirm(true);
  };

  const handleSaveBedrock = () => {
    setPendingSection('bedrock');
    setShowBedrockConfirm(true);
  };

  const confirmSave = () => {
    if (!pendingSection) return;

    let updates: Array<{ key: string; value: string | number | boolean }> = [];

    switch (pendingSection) {
      case 'prompts':
        updates = [
          { key: 'ai_suggestions_system_prompt_freelancer', value: aiSuggestionsPromptFreelancer },
          { key: 'ai_suggestions_system_prompt_client', value: aiSuggestionsPromptClient },
          { key: 'ai_helper_system_prompt_freelancer', value: aiHelperPromptFreelancer },
          { key: 'ai_helper_system_prompt_client', value: aiHelperPromptClient },
        ];
        setShowPromptsConfirm(false);
        break;
      case 'general':
        updates = [
          { key: 'ai_suggestions_message_count', value: messageCount },
          { key: 'email_notification_threshold_hours', value: emailThreshold },
          { key: 'rag_enabled', value: ragEnabled },
          { key: 'analysis_batch_size', value: analysisBatchSize },
          { key: 'max_retrieval_results', value: maxRetrievalResults },
        ];
        setShowGeneralConfirm(false);
        break;
      case 'bedrock':
        updates = [
          { key: 'ai_model', value: modelId },
          { key: 'bedrock_knowledge_base_id', value: knowledgeBaseId },
          { key: 'bedrock_data_source_id', value: dataSourceId },
        ];
        setShowBedrockConfirm(false);
        break;
    }

    updateConfigMutation.mutate(updates);
    setPendingSection(null);
  };

  const cancelSave = () => {
    setShowPromptsConfirm(false);
    setShowGeneralConfirm(false);
    setShowBedrockConfirm(false);
    setPendingSection(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Role-Based System Prompts */}
      <div className="bg-white rounded-[40px] border border-slate-200 p-6 shadow-sm">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Role-Based System Prompts</h3>
            <p className="text-sm text-slate-500 mt-1">Configure role-specific AI behavior for suggestions and helper. AI suggestions and helper will use these prompts based on the user's role (freelancer/genius or client).</p>
          </div>
          {!isEditingPrompts ? (
            <Button
              onClick={handleEditPrompts}
              variant="outline"
              className="rounded-[40px] border-slate-200 h-10"
            >
              <Edit2 className="h-4 w-4 mr-2" />
              Edit
            </Button>
          ) : (
            <Button
              onClick={handleCancelPrompts}
              variant="outline"
              className="rounded-[40px] border-slate-200 h-10"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          )}
        </div>
        <div className="space-y-6">
          <div className="space-y-6">
            <div>
              <Label className="text-base font-semibold text-slate-900">AI Suggestions</Label>
              <div className="mt-3 space-y-4">
                <div>
                  <Label htmlFor="ai-suggestions-prompt-freelancer" className="text-sm text-slate-600 mb-1.5 block">Freelancer (Genius) System Prompt</Label>
                  <Textarea
                    id="ai-suggestions-prompt-freelancer"
                    value={aiSuggestionsPromptFreelancer}
                    onChange={(e) => setAiSuggestionsPromptFreelancer(e.target.value)}
                    rows={3}
                    placeholder="Enter system prompt for freelancer AI suggestions..."
                    className="rounded-[16px] border-slate-200"
                    disabled={!isEditingPrompts}
                  />
                </div>
                <div>
                  <Label htmlFor="ai-suggestions-prompt-client" className="text-sm text-slate-600 mb-1.5 block">Client System Prompt</Label>
                  <Textarea
                    id="ai-suggestions-prompt-client"
                    value={aiSuggestionsPromptClient}
                    onChange={(e) => setAiSuggestionsPromptClient(e.target.value)}
                    rows={3}
                    placeholder="Enter system prompt for client AI suggestions..."
                    className="rounded-[16px] border-slate-200"
                    disabled={!isEditingPrompts}
                  />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-base font-semibold text-slate-900">AI Helper</Label>
              <div className="mt-3 space-y-4">
                <div>
                  <Label htmlFor="ai-helper-prompt-freelancer" className="text-sm text-slate-600 mb-1.5 block">Freelancer (Genius) System Prompt</Label>
                  <Textarea
                    id="ai-helper-prompt-freelancer"
                    value={aiHelperPromptFreelancer}
                    onChange={(e) => setAiHelperPromptFreelancer(e.target.value)}
                    rows={3}
                    placeholder="Enter system prompt for freelancer AI helper..."
                    className="rounded-[16px] border-slate-200"
                    disabled={!isEditingPrompts}
                  />
                </div>
                <div>
                  <Label htmlFor="ai-helper-prompt-client" className="text-sm text-slate-600 mb-1.5 block">Client System Prompt</Label>
                  <Textarea
                    id="ai-helper-prompt-client"
                    value={aiHelperPromptClient}
                    onChange={(e) => setAiHelperPromptClient(e.target.value)}
                    rows={3}
                    placeholder="Enter system prompt for client AI helper..."
                    className="rounded-[16px] border-slate-200"
                    disabled={!isEditingPrompts}
                  />
                </div>
              </div>
            </div>
          </div>
          {isEditingPrompts && (
            <div className="flex justify-end pt-4 border-t border-slate-200">
              <Button
                onClick={handleSavePrompts}
                disabled={updateConfigMutation.isPending}
                className="rounded-[40px] bg-[#4669ed] hover:bg-[#3654c9] text-white font-bold px-5 h-12"
              >
                {updateConfigMutation.isPending && pendingSection === 'prompts' ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Prompts
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* General Settings */}
      <div className="bg-white rounded-[40px] border border-slate-200 p-6 shadow-sm">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-900">General Settings</h3>
          </div>
          {!isEditingGeneral ? (
            <Button
              onClick={handleEditGeneral}
              variant="outline"
              className="rounded-[40px] border-slate-200 h-10"
            >
              <Edit2 className="h-4 w-4 mr-2" />
              Edit
            </Button>
          ) : (
            <Button
              onClick={handleCancelGeneral}
              variant="outline"
              className="rounded-[40px] border-slate-200 h-10"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          )}
        </div>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="message-count" className="text-sm font-semibold text-slate-900">AI Suggestions Message Count</Label>
              <p className="text-sm text-slate-500 mt-1">Number of last messages to use for suggestions</p>
            </div>
            <Input
              id="message-count"
              type="number"
              value={messageCount}
              onChange={(e) => setMessageCount(Number(e.target.value))}
              className="w-24 rounded-[40px] border-slate-200 h-12"
              min={1}
              disabled={!isEditingGeneral}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="email-threshold" className="text-sm font-semibold text-slate-900">Email Notification Threshold (hours)</Label>
              <p className="text-sm text-slate-500 mt-1">Hours before sending inactivity reminder</p>
            </div>
            <Input
              id="email-threshold"
              type="number"
              value={emailThreshold}
              onChange={(e) => setEmailThreshold(Number(e.target.value))}
              className="w-24 rounded-[40px] border-slate-200 h-12"
              min={1}
              disabled={!isEditingGeneral}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="rag-enabled" className="text-sm font-semibold text-slate-900">RAG Enabled</Label>
              <p className="text-sm text-slate-500 mt-1">Enable knowledge base retrieval</p>
            </div>
            <Switch
              id="rag-enabled"
              checked={ragEnabled}
              onCheckedChange={(checked) => {
                setRagEnabled(checked);
              }}
              disabled={!isEditingGeneral}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="max-retrieval" className="text-sm font-semibold text-slate-900">Max Retrieval Results</Label>
              <p className="text-sm text-slate-500 mt-1">Maximum chunks to retrieve from knowledge base</p>
            </div>
            <Input
              id="max-retrieval"
              type="number"
              value={maxRetrievalResults}
              onChange={(e) => setMaxRetrievalResults(Number(e.target.value))}
              className="w-24 rounded-[40px] border-slate-200 h-12"
              min={1}
              max={20}
              disabled={!isEditingGeneral}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="analysis-batch-size" className="text-sm font-semibold text-slate-900">Analysis Batch Size</Label>
              <p className="text-sm text-slate-500 mt-1">Number of conversations to process per batch in nightly analysis</p>
            </div>
            <Input
              id="analysis-batch-size"
              type="number"
              value={analysisBatchSize}
              onChange={(e) => setAnalysisBatchSize(Number(e.target.value))}
              className="w-24 rounded-[40px] border-slate-200 h-12"
              min={1}
              max={50}
              disabled={!isEditingGeneral}
            />
          </div>
          {isEditingGeneral && (
            <div className="flex justify-end pt-4 border-t border-slate-200">
              <Button
                onClick={handleSaveGeneral}
                disabled={updateConfigMutation.isPending}
                className="rounded-[40px] bg-[#4669ed] hover:bg-[#3654c9] text-white font-bold px-5 h-12"
              >
                {updateConfigMutation.isPending && pendingSection === 'general' ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Settings
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Bedrock Configuration */}
      <div className="bg-white rounded-[40px] border border-slate-200 p-6 shadow-sm">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-900">AWS Bedrock Configuration</h3>
            <p className="text-sm text-slate-500 mt-1">Configure Bedrock Knowledge Base settings</p>
          </div>
          {!isEditingBedrock ? (
            <Button
              onClick={handleEditBedrock}
              variant="outline"
              className="rounded-[40px] border-slate-200 h-10"
            >
              <Edit2 className="h-4 w-4 mr-2" />
              Edit
            </Button>
          ) : (
            <Button
              onClick={handleCancelBedrock}
              variant="outline"
              className="rounded-[40px] border-slate-200 h-10"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          )}
        </div>
        <div className="space-y-4">
          <div>
            <Label htmlFor="model-id" className="text-sm text-slate-600 mb-1.5 block">AI Model ID</Label>
            <Input
              id="model-id"
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
              placeholder="anthropic.claude-3-5-haiku-20241022"
              className="rounded-[40px] border-slate-200 h-12"
              disabled={!isEditingBedrock}
            />
          </div>

          <div>
            <Label htmlFor="knowledge-base-id" className="text-sm text-slate-600 mb-1.5 block">Knowledge Base ID</Label>
            <Input
              id="knowledge-base-id"
              value={knowledgeBaseId}
              onChange={(e) => setKnowledgeBaseId(e.target.value)}
              placeholder="Enter Bedrock Knowledge Base ID"
              className="rounded-[40px] border-slate-200 h-12"
              disabled={!isEditingBedrock}
            />
          </div>

          <div>
            <Label htmlFor="data-source-id" className="text-sm text-slate-600 mb-1.5 block">Data Source ID</Label>
            <Input
              id="data-source-id"
              value={dataSourceId}
              onChange={(e) => setDataSourceId(e.target.value)}
              placeholder="Enter Bedrock Data Source ID"
              className="rounded-[40px] border-slate-200 h-12"
              disabled={!isEditingBedrock}
            />
          </div>
          {isEditingBedrock && (
            <div className="flex justify-end pt-4 border-t border-slate-200">
              <Button
                onClick={handleSaveBedrock}
                disabled={updateConfigMutation.isPending}
                className="rounded-[40px] bg-[#4669ed] hover:bg-[#3654c9] text-white font-bold px-5 h-12"
              >
                {updateConfigMutation.isPending && pendingSection === 'bedrock' ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Bedrock Config
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Knowledge Base */}
      <div className="bg-white rounded-[40px] border border-slate-200 p-6 shadow-sm">
        <div className="mb-6">
          <h3 className="text-lg font-bold text-slate-900">Knowledge Base</h3>
          <p className="text-sm text-slate-500 mt-1">Manage documents and Bedrock knowledge base</p>
        </div>

        {/* Upload Section */}
        <div className="mb-6 pb-6 border-b border-slate-200">
          <div className="mb-4">
            <h4 className="text-base font-semibold text-slate-900">Upload Document</h4>
            <p className="text-sm text-slate-500 mt-1">Upload documents to the knowledge base (PDF, DOCX, TXT, MD)</p>
          </div>
          <div className="flex items-center gap-4">
            <Input
              type="file"
              accept=".pdf,.docx,.txt,.md"
              onChange={handleFileUpload}
              disabled={uploadMutation.isPending}
              className="flex-1 cursor-pointer rounded-[40px] border-slate-200 h-12 p-3"
            />
            {uploadMutation.isPending && (
              <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
            )}
          </div>
        </div>

        {/* Documents List */}
        <div className="mb-6 pb-6 border-b border-slate-200">
          <div className="mb-4">
            <h4 className="text-base font-semibold text-slate-900">Documents</h4>
            <p className="text-sm text-slate-500 mt-1">Manage knowledge base documents</p>
          </div>
          {loadingDocuments ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : documents && documents.length > 0 ? (
            <div className="space-y-3">
              {documents.map((doc: any) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-4 border border-slate-200 rounded-[16px]"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <FileText className="h-5 w-5 text-slate-400" />
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{doc.file_name}</p>
                      <div className="flex items-center gap-4 mt-1">
                        <span className="text-xs text-slate-500">
                          {format(new Date(doc.created_at), 'MMM d, yyyy')}
                        </span>
                        <span className="text-xs text-slate-500">
                          {(doc.file_size / 1024).toFixed(1)} KB
                        </span>
                        <span className={cn("text-xs px-3 py-1 rounded-full font-semibold", getStatusColor(doc.status))}>
                          {doc.status}
                        </span>
                      </div>
                      {doc.sync_error && (
                        <p className="text-xs text-red-600 mt-1">{doc.sync_error}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          const url = await adminService.getDocumentUrl(doc.id);
                          window.open(url, '_blank');
                        } catch (_error) {
                          toast.error('Failed to get document URL');
                        }
                      }}
                      className="rounded-[40px] border-slate-200 h-9"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => syncMutation.mutate(doc.id)}
                      disabled={syncMutation.isPending}
                      className="rounded-[40px] border-slate-200 h-9"
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Sync
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteMutation.mutate(doc.id)}
                      disabled={deleteMutation.isPending}
                      className="rounded-[40px] border-slate-200 h-9"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500 text-center py-8">No documents uploaded yet</p>
          )}
        </div>

        {/* Test Query */}
        <div className="mb-6 pb-6 border-b border-slate-200">
          <div className="mb-4">
            <h4 className="text-base font-semibold text-slate-900">Test Query</h4>
            <p className="text-sm text-slate-500 mt-1">Test Bedrock knowledge base retrieval</p>
          </div>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={testQuery}
                onChange={(e) => setTestQuery(e.target.value)}
                placeholder="Enter a test query..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleTestQuery();
                  }
                }}
                className="rounded-[40px] border-slate-200 h-12"
              />
              <Button
                onClick={handleTestQuery}
                disabled={!testQuery.trim() || testQueryMutation.isPending}
                className="rounded-[40px] bg-[#4669ed] hover:bg-[#3654c9] text-white font-bold h-12 px-5"
              >
                {testQueryMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>
            </div>

            {testResults && (
              <div className="space-y-3">
                <p className="text-sm font-semibold text-slate-900">Results:</p>
                {testResults.length > 0 ? (
                  testResults.map((result: any, index: number) => (
                    <div key={index} className="p-4 border border-slate-200 rounded-[16px] bg-slate-50">
                      <p className="text-sm text-slate-700">{result.content}</p>
                      {result.score && (
                        <p className="text-xs text-slate-500 mt-2">Score: {result.score.toFixed(3)}</p>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">No results found</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sync Status */}
        {syncStatus && (
          <div>
            <div className="mb-4">
              <h4 className="text-base font-semibold text-slate-900">Sync Status</h4>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-slate-600">
                  Overall Status: <span className="font-semibold capitalize text-slate-900">{syncStatus.status || 'Unknown'}</span>
                </p>
                {syncStatus.hasActiveSync && (
                  <p className="text-xs text-[#4669ed] mt-1 font-semibold">Active sync in progress</p>
                )}
              </div>
              {syncStatus.summary && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pt-4 border-t border-slate-200">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Total</p>
                    <p className="text-sm font-semibold text-slate-900">{syncStatus.summary.total || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Completed</p>
                    <p className="text-sm font-semibold text-emerald-600">{syncStatus.summary.completed || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Syncing</p>
                    <p className="text-sm font-semibold text-[#4669ed]">{syncStatus.summary.syncing || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Failed</p>
                    <p className="text-sm font-semibold text-red-600">{syncStatus.summary.failed || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Uploading</p>
                    <p className="text-sm font-semibold text-amber-600">{syncStatus.summary.uploading || 0}</p>
                  </div>
                </div>
              )}
              {syncStatus.latestSyncAt && (
                <p className="text-xs text-slate-500 pt-4 border-t border-slate-200">
                  Last sync: {new Date(syncStatus.latestSyncAt).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Dialogs */}
      <AlertDialog open={showPromptsConfirm} onOpenChange={setShowPromptsConfirm}>
        <AlertDialogContent className="rounded-[24px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Save Role-Based System Prompts?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to save the changes to the role-based system prompts? This will update all AI suggestions and helper prompts for both freelancers and clients.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelSave} className="rounded-[40px]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSave} className="rounded-[40px] bg-[#4669ed] hover:bg-[#3654c9]">Save</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showGeneralConfirm} onOpenChange={setShowGeneralConfirm}>
        <AlertDialogContent className="rounded-[24px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Save General Settings?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to save the changes to the general settings? This will update message count, email threshold, RAG settings, and retrieval results.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelSave} className="rounded-[40px]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSave} className="rounded-[40px] bg-[#4669ed] hover:bg-[#3654c9]">Save</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showBedrockConfirm} onOpenChange={setShowBedrockConfirm}>
        <AlertDialogContent className="rounded-[24px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Save AWS Bedrock Configuration?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to save the changes to the AWS Bedrock configuration? This will update the AI model, knowledge base ID, and data source ID.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelSave} className="rounded-[40px]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSave} className="rounded-[40px] bg-[#4669ed] hover:bg-[#3654c9]">Save</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

